import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wand2, Sparkles, Loader2, Target, X } from "lucide-react";
import { API, useAuth } from "@/App";
import { ChipBtn, FieldGroup, SectionCard, WipNote } from "./ClientDialerUi";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const PRESET_KEY = "client_dialer_presets";
const SORT_LABEL = { open_ar_desc: "Highest Open AR first", open_ar_asc: "Lowest Open AR first", tickets_desc: "Most open tickets first",
  providers_desc: "Most providers first", clinic_asc: "Practice name A–Z", client_id_asc: "Client # ascending" };
const RANGES = [["open_ar", "Open AR ($)"], ["tickets", "Tickets open"], ["providers", "Providers"]];
const EXAMPLES = ["Texas clients with open AR over $5k", "Clients with open support tickets", "Dormant clients using CCM, most providers first"];

const hasIntent = (f) => Object.entries(f || {}).some(([, v]) => (Array.isArray(v) ? v.length : v !== "" && v != null));
const loadPresets = () => { try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "[]"); } catch { return []; } };

function AIPanel({ prompt, setPrompt, onApplied }) {
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(0);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/client-dialer/lumina`, { prompt }, { headers: { Authorization: `Bearer ${token}` } });
      onApplied(r.data);
      setApplied(Object.keys(r.data.filters || {}).length);
    } catch (e) { toast.error(e?.response?.data?.detail || "Lumina is unavailable"); }
    setBusy(false);
  };
  return (
    <div data-testid="cd-ai-panel" style={{ padding: 14, borderRadius: 12, background: "linear-gradient(135deg, #EEF6FF 0%, #F4EAFE 100%)", border: "0.5px solid #cfe2ff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: 8, background: "linear-gradient(135deg, var(--blue), #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center" }}><Wand2 style={{ width: 13, height: 13, color: "#fff" }} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Describe your client list in plain English</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>Powered by <strong>LUMINA</strong> — understands state, status, services, risk, owner, open AR, open tickets, providers and practice name.</div>
        </div>
      </div>
      <textarea data-testid="cd-lumina-input" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="input"
        placeholder='e.g. "Texas clients with open AR over $5k and open tickets, owned by Maria"'
        style={{ resize: "vertical", fontSize: 12, fontFamily: "inherit", marginBottom: 8, minHeight: 90, lineHeight: 1.45 }} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {EXAMPLES.map((e, i) => (
          <button key={i} type="button" data-testid={`cd-ai-example-${i}`} onClick={() => setPrompt(e)} title={e}
            style={{ fontSize: 10, padding: "3px 8px", borderRadius: 999, background: "var(--paper)", border: "0.5px solid var(--border)", color: "var(--text-3)", cursor: "pointer", fontFamily: "inherit", maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" data-testid="cd-lumina-ask" onClick={submit} disabled={busy || !prompt.trim()} className="btn btn-primary" style={{ padding: "6px 14px", fontSize: 12, opacity: busy || !prompt.trim() ? 0.6 : 1 }}>
          {busy ? <><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Parsing…</> : <><Sparkles style={{ width: 12, height: 12 }} /> Parse with AI & apply</>}
        </button>
        {applied > 0 && <span data-testid="cd-ai-applied" style={{ fontSize: 10.5, fontWeight: 600, color: "#1a7a52" }}>✓ {applied} filter{applied === 1 ? "" : "s"} applied</span>}
      </div>
    </div>
  );
}

function Presets({ f, setF, setSort, sort }) {
  const [presets, setPresets] = useState(loadPresets);
  const save = () => {
    const label = window.prompt("Name this preset");
    if (!label?.trim()) return;
    const next = [...presets, { id: `p${Date.now()}`, label: label.trim(), filters: f, sort }];
    setPresets(next); localStorage.setItem(PRESET_KEY, JSON.stringify(next)); toast.success("Preset saved");
  };
  return (
    <FieldGroup eyebrow="Presets" hint={`${presets.length} saved`}>
      <div data-testid="cd-presets" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {presets.map((p) => <ChipBtn key={p.id} testid={`cd-preset-${p.id}`} onClick={() => { setF(p.filters || {}); setSort(p.sort || "open_ar_desc"); }}>{p.label}</ChipBtn>)}
        <button type="button" data-testid="cd-save-preset" onClick={save} style={{ padding: "4px 11px", borderRadius: 999, fontSize: 11, fontWeight: 500, border: "0.5px dashed var(--border)", background: "transparent", color: "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>+ Save current</button>
      </div>
    </FieldGroup>
  );
}

function Chips({ k, values, f, toggle, label = (v) => v }) {
  return (
    <div data-testid={`cd-filter-${k}`} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {values.map((v) => <ChipBtn key={v} testid={`cd-filter-${k}-${v}`} active={(f[k] || []).includes(v)} onClick={() => toggle(k, v)}>{label(v)}</ChipBtn>)}
      {values.length === 0 && <span style={{ fontSize: 11, color: "var(--text-4)" }}>No values on file</span>}
    </div>
  );
}

function Filters({ f, setF, opts }) {
  const toggle = (k, v) => { const cur = f[k] || []; setF({ ...f, [k]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] }); };
  const owners = opts.owners || [];
  const num = (k) => (e) => setF({ ...f, [k]: e.target.value === "" ? "" : Number(e.target.value) });
  return (<>
    <SectionCard title="Client profile">
      <FieldGroup eyebrow="Practice name or client #">
        <input data-testid="cd-filter-search" className="input" value={f.search || ""} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="e.g. Demo Clinic or 2330" style={{ fontSize: 12 }} />
      </FieldGroup>
      <FieldGroup eyebrow="Status"><Chips k="statuses" values={opts.statuses || []} f={f} toggle={toggle} /></FieldGroup>
      <FieldGroup eyebrow="Services (client has all)"><Chips k="services" values={opts.services || []} f={f} toggle={toggle} /></FieldGroup>
      <FieldGroup eyebrow="Risk"><Chips k="risks" values={opts.risks || []} f={f} toggle={toggle} /></FieldGroup>
      <FieldGroup eyebrow="Owner"><Chips k="owner_ids" values={owners.map((o) => o[0])} f={f} toggle={toggle} label={(id) => owners.find((o) => o[0] === id)?.[1] || id} /></FieldGroup>
    </SectionCard>
    <SectionCard title="Account health">
      {RANGES.map(([k, l]) => (
        <div key={k} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FieldGroup eyebrow={`${l} ≥`}><input data-testid={`cd-filter-${k}-min`} type="number" className="input" value={f[`${k}_min`] ?? ""} onChange={num(`${k}_min`)} style={{ fontSize: 12 }} /></FieldGroup>
          <FieldGroup eyebrow={`${l} ≤`}><input data-testid={`cd-filter-${k}-max`} type="number" className="input" value={f[`${k}_max`] ?? ""} onChange={num(`${k}_max`)} style={{ fontSize: 12 }} /></FieldGroup>
        </div>
      ))}
    </SectionCard>
    <SectionCard title="Geography">
      <FieldGroup eyebrow="States"><Chips k="states" values={opts.states || []} f={f} toggle={toggle} /></FieldGroup>
    </SectionCard>
    <WipNote testid="cd-filters-wip">More client-specific filters are coming soon for client campaigns.</WipNote>
  </>);
}

function PreviewRow({ r, canExclude, onExclude }) {
  return (
    <div data-testid={`cd-preview-row-${r.client_id}`} style={{ padding: "8px 10px", border: "0.5px solid var(--border-soft, #f1f5f9)", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.clinic} <span style={{ color: "#94a3b8", fontWeight: 400 }}>#{r.client_id}</span></div>
        <div style={{ fontSize: 10.5, color: "var(--text-3)" }}>{[r.state_code, r.status, r.services.join(", "), `${money(r.open_ar)} AR`, `${r.tickets_open} tickets`, r.owner_name].filter(Boolean).join(" · ")}</div>
      </div>
      {canExclude && <button type="button" data-testid={`cd-exclude-${r.client_id}`} onClick={() => onExclude(r.client_id)} title="Exclude this client" style={{ border: "0.5px solid #FCA5A5", background: "#FEF2F2", color: "#A32D2D", borderRadius: 6, padding: "3px 6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontFamily: "inherit" }}><X style={{ width: 10, height: 10 }} /> Exclude</button>}
    </div>
  );
}

function Preview({ f, setF, sort, setSort, campaignId, luminaPinned, onCount }) {
  const { token } = useAuth();
  const [limit, setLimit] = useState(25);
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(false);
  const empty = !hasIntent(f);
  useEffect(() => { setLimit(25); }, [JSON.stringify(f), sort]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      axios.post(`${API}/client-dialer/preview`, { filters: f, sort, campaign_id: campaignId, limit }, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => { setPv(r.data); onCount(r.data.count); }).catch(() => setPv(null)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [JSON.stringify(f), sort, limit]); // eslint-disable-line react-hooks/exhaustive-deps
  const exclude = (id) => { setF({ ...f, exclude_ids: [...(f.exclude_ids || []), id] }); toast.success("Client excluded from campaign"); };
  return (
    <div data-testid="cd-preview" className="card" style={{ padding: 18, border: "0.5px solid var(--border)", minHeight: 320, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Target style={{ width: 14, height: 14, color: "var(--blue)" }} />
        <span style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Audience preview</span>
        {luminaPinned && <span data-testid="cd-preview-lumina" style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "var(--blue-soft, #E6F1FB)", color: "var(--blue, #185FA5)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>✦ Lumina</span>}
        {loading && <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", color: "var(--text-3)", marginLeft: "auto" }} />}
      </div>
      {empty && <div data-testid="cd-preview-hint" style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 10 }}>No filters yet — every available client matches. Describe your audience or toggle a filter to narrow it.</div>}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span data-testid="cd-preview-count" className="num" style={{ fontSize: 36, fontWeight: 700, color: "var(--navy)", letterSpacing: "-0.02em", lineHeight: 1 }}>{pv?.count ?? 0}</span>
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>{pv?.count === 1 ? "client matches" : "clients match"}</span>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--text-4)", marginBottom: 10 }}>
        {pv?.in_other_campaigns ? `${pv.in_other_campaigns} already in another campaign · ` : ""}Do Not Call clients are left out
        {(f.exclude_ids || []).length > 0 && (<> · {f.exclude_ids.length} excluded <button type="button" data-testid="cd-restore-excluded" onClick={() => setF({ ...f, exclude_ids: [] })} style={{ border: "none", background: "none", color: "var(--blue)", cursor: "pointer", fontSize: 10.5, padding: 0, fontFamily: "inherit" }}>Restore</button></>)}
      </div>
      <select data-testid="cd-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input" style={{ fontSize: 11, marginBottom: 10 }}>
        {Object.entries(SORT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <div style={{ borderTop: "0.5px solid var(--border-soft)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 6, overflowY: "auto", maxHeight: 420 }}>
        {pv && pv.preview.length === 0 && <div style={{ fontSize: 11, color: "var(--text-4)", textAlign: "center", padding: "20px 0", fontStyle: "italic" }}>No clients match the current filters yet.</div>}
        {(pv?.preview || []).map((r) => <PreviewRow key={r.client_id} r={r} canExclude={luminaPinned} onExclude={exclude} />)}
        {pv && pv.preview.length < pv.count && (
          <button type="button" data-testid="cd-preview-load-more" className="btn" onClick={() => setLimit((l) => Math.min(l + 25, 500))} style={{ fontSize: 11, justifyContent: "center" }}>Load more ({pv.count - pv.preview.length} more)</button>
        )}
      </div>
    </div>
  );
}

/** Step 2 — Client list. Same two-column layout as the Leads audience step. */
export default function ClientAudienceStep({ f, setF, sort, setSort, prompt, setPrompt, opts, campaignId, onCount }) {
  const [luminaPinned, setLuminaPinned] = useState(false);
  const setFilters = (next) => setF(next);
  const onApplied = (d) => { setF(d.filters || {}); setSort(d.sort || "open_ar_desc"); setLuminaPinned(true); };
  return (
    <div data-testid="cd-step2" style={{ display: "grid", gridTemplateColumns: "1.3fr 0.85fr", gap: 18 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <AIPanel prompt={prompt} setPrompt={setPrompt} onApplied={onApplied} />
        <Presets f={f} setF={setFilters} sort={sort} setSort={setSort} />
        <Filters f={f} setF={setFilters} opts={opts} />
      </div>
      <div style={{ position: "sticky", top: 0, alignSelf: "flex-start" }}>
        <Preview f={f} setF={setF} sort={sort} setSort={setSort} campaignId={campaignId} luminaPinned={luminaPinned} onCount={onCount} />
      </div>
    </div>
  );
}
